import { Injectable, EventEmitter } from '@angular/core';
import createAuth0Client, { Auth0Client, GetTokenSilentlyOptions } from '@auth0/auth0-spa-js';

import { environment as env } from '../../../environments/environment';

import { from, of, Observable, BehaviorSubject, combineLatest, throwError } from 'rxjs';
import { tap, map, catchError, concatMap, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class LgvAuth0Service {
  OnAuthenticatedEvent: EventEmitter<any> = new EventEmitter();

  private urlKey = 'Lgv_OrigURL';
  private useSecondaryAuthConfigKey = 'UseSecondaryAuthConfig';
  private _useSecondaryAuthConfig: boolean = false;
  public origAuthSettings: any;

  // Observables and Subjects
  private userProfileSubject$ = new BehaviorSubject<any>(null);
  public userProfile$ = this.userProfileSubject$.asObservable();
  public isAuthenticated$: Observable<boolean>;
  public auth0Client$: Observable<Auth0Client>;
  public handleRedirectCallback$: Observable<any>;
  public loggedIn: boolean = null;

  constructor(private router: Router) {
    // Read the flag from local storage
    const useSecondaryConfig = localStorage.getItem(this.useSecondaryAuthConfigKey);
    this.UseSecondaryAuthConfig = useSecondaryConfig === 'true';
  
    this.origAuthSettings = this.AuthSettings;
  
    // Initialize Auth0 client observables
    this.initializeAuth0Client();
  
    const curUrl = window.location.href;
    if (!curUrl.includes('code=')) {
      this.OrigURL = window.location.href;
    }
  
    // Proceed with authentication setup
    this.localAuthSetup();
    this.handleAuthCallback();
  }
  
  // Getter and Setter for OrigURL
  get OrigURL(): string {
    return localStorage.getItem(this.urlKey);
  }
  set OrigURL(value: string) {
    localStorage.setItem(this.urlKey, value);
  }

  // Getter and Setter for UseSecondaryAuthConfig
  get UseSecondaryAuthConfig(): boolean {
    return this._useSecondaryAuthConfig;
  }
  set UseSecondaryAuthConfig(value: boolean) {
    this._useSecondaryAuthConfig = value;
    if (value) {
      localStorage.setItem(this.useSecondaryAuthConfigKey, 'true');
    } else {
      localStorage.removeItem(this.useSecondaryAuthConfigKey);
    }
  }

  // Determines which Auth0 configuration to use
  get AuthSettings(): any {
    return this.UseSecondaryAuthConfig ? env.PasswordAuth0Config : env.Auth0Config;
  }

  // Checks if the Auth0 tenant needs to be reset
  get Auth0TenantNeedsReset(): boolean {
    return this.origAuthSettings !== this.AuthSettings;
  }

  // Initializes the Auth0 client and related observables
  private initializeAuth0Client() {

    this.auth0Client$ = from(
      createAuth0Client({
        domain: this.AuthSettings.domain,
        client_id: this.AuthSettings.clientID,
        audience: this.AuthSettings.audience,
        redirect_uri: `${window.location.origin}`,
        useRefreshTokens: true,
        cacheLocation: 'localstorage' // Add this line
      })
    ).pipe(
      tap(() => console.log('Auth0 Client initialized successfully')),
      shareReplay(1),
      catchError((err) => {
        console.error('Error initializing Auth0 Client', err);
        return throwError(err);
      })
    );

    this.isAuthenticated$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.isAuthenticated())),
      tap((res) => (this.loggedIn = res))
    );

    this.handleRedirectCallback$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.handleRedirectCallback()))
    );

    // Reset user profile subject
    this.userProfileSubject$ = new BehaviorSubject<any>(null);
    this.userProfile$ = this.userProfileSubject$.asObservable();
  }

  // Resets the Auth0 tenant when switching configurations
  ResetAuth0Tenant() {
    console.log(`Changing AuthType ${this.origAuthSettings.clientID} ${this.origAuthSettings.audience} -> ${this.AuthSettings.clientID} ${this.AuthSettings.audience}`);

    this.origAuthSettings = this.AuthSettings;

    this.initializeAuth0Client();
    this.loggedIn = null;
  }

  // Sets up local authentication streams
  private localAuthSetup() {
    this.auth0Client$.subscribe(
      () => {
        const checkAuth$ = this.isAuthenticated$.pipe(
          concatMap((loggedIn: boolean) => {
            if (loggedIn) {
              // If authenticated, get user and set in app
              this.OnAuthenticatedEvent.emit(loggedIn);
              return this.getUser$();
            }

            // If not authenticated, return stream that emits 'false'
            return of(loggedIn);
          })
        );
        checkAuth$.subscribe();
      },
      (err) => {
        console.error('Error during Auth0 Client initialization', err);
      }
    );
  }

  // Initiates the login process
  login(useSecondaryConfig: boolean = false) {
    this.UseSecondaryAuthConfig = useSecondaryConfig;
    if (this.Auth0TenantNeedsReset) {
      this.ResetAuth0Tenant();
    }
    
    this.OrigURL = window.location.href;

    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        redirect_uri: `${window.location.origin}`,
        appState: { target: this.OrigURL },
      });
    });
  }

  private handleAuthCallback() {
    const params = window.location.search;
  
    if (params && params.length > 0) {
      const urlParams = new URLSearchParams(params);
  
      // Handle authentication errors
      if (params.includes('error=')) {
        console.log('Error Message: ' + (urlParams.get('error_description') || urlParams.get('error')));

        this.UseSecondaryAuthConfig = false;
  
        // Navigate to an error page or show a message
        const errorDescription = urlParams.get('error_description') || 'Authentication failed.';
        this.router.navigate(['/default/error', errorDescription]);
        return;
      }
    }
  
    if (params.includes('code=') && params.includes('state=')) {
      let targetRoute: string;
      const authComplete$ = this.handleRedirectCallback$.pipe(

        tap((cbRes) => {
          // Get and set target redirect route from callback results
          targetRoute = cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/';
        }),
        concatMap(() => {
          // Redirect callback complete; get user and login status
          return combineLatest([this.getUser$(), this.isAuthenticated$]);
        })
      );
  
      authComplete$.subscribe(
        ([user, loggedIn]) => {
          this.OnAuthenticatedEvent.emit(loggedIn);

          this.router.navigate([targetRoute]);
        },
        (err) => {
          // Handle errors during authentication
          console.error('Error during authentication callback', err);
          // Reset the flag upon authentication failure
          this.UseSecondaryAuthConfig = false;
          // Navigate to an error page
          this.router.navigate(['/default/error', 'Authentication failed.']);
        }
      );
    }
  }
  
  logout() {
    // Reset the flag
    this.UseSecondaryAuthConfig = false;

    this.auth0Client$.subscribe((client: Auth0Client) => {
      client.logout({
        client_id: this.AuthSettings.clientID,
        returnTo: window.location.origin,
      });

      this.OnAuthenticatedEvent.emit(false);
    });
  }

  getTokenSilently$(options?: GetTokenSilentlyOptions): Observable<string> {
    if (!this.auth0Client$) {
      console.error('Auth0 Client is not initialized');
      return throwError('Auth0 Client is not initialized');
    }

    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) =>
        from(
          client.getTokenSilently({
            ...options,
            detailedResponse: false,
          })
        )
      )
    );
  }

  getUser$(options?): Observable<any> {
    if (!this.auth0Client$) {
      console.error('Auth0 Client is not initialized');
      return throwError('Auth0 Client is not initialized');
    }

    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser(options))),
      tap((user) => this.userProfileSubject$.next(user))
    );
  }

  get userRoles$() { return this.getUser$().pipe(
      map((x) => {
        return x['https://longevity.com/roles'];
      })
    );
  }

  hasRole$(roleName: string): Observable<boolean> {
    return this.userRoles$.pipe(map((x) => x.indexOf(roleName) !== -1));
  }
}
